const QUERY_KEYS_SHIFT = {
  shifts: () => [...QUERY_KEYS.all(), 'shifts'],
  shift: (shiftId: string) => [...QUERY_KEYS.shifts(), { shiftId }],
  shiftClaimsWithFilters: (filters?: Record<string, unknown>) => [
    ...QUERY_KEYS.shifts(),
    'shift-claims',
    filters,
  ],
  shiftApplication: (shiftApplicationId: string) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'shift-application',
    { shiftApplicationId },
  ],
  shiftClaimsForFlexWorker: (filters: { [key: string]: string }) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'shiftClaimsForFlexWorker',
    { filters },
  ],
  shiftPartner: (shiftId: string) => [...QUERY_KEYS_SHIFT.shifts(), 'shift-partners', { shiftId }],
  shiftEligiblePartners: (shiftId: string) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'eligible-partners',
    { shiftId },
  ],
  shiftPlanningShiftsFilters: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'shift-planning-shifts',
    { params },
  ],
  plannerShiftsPartners: (shiftIds: string[]) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'planner-shifts-partners',
    ...shiftIds,
  ],
  shiftPlanning: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'shift-planning',
    { params },
  ],
  shiftPlanningWithFilters: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'shift-planning-with-filters',
    { params },
  ],
  preferredWorkerProposalsWithFilters: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'preferred-worker-proposals',
    { params },
  ],
  shiftPlanningShiftDetail: (shiftId: string) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'shiftPlanningShiftDetail',
    shiftId,
  ],
  shiftPlanningShiftPopoverDetail: (shiftId: string | undefined) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'shiftPlanningShiftPopoverDetail',
    shiftId,
  ],
  shiftPlanningShiftWorkers: (shiftId: string | undefined, fullNameSearch?: string) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'workers',
    { shiftId, fullNameSearch },
  ],
  shiftPlanningShiftClaimants: (shiftId: string) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'claimants',
    { shiftId },
  ],
  breakOptions: () => [...QUERY_KEYS_SHIFT.shifts(), 'shift-bulk-edit-options'],
  preferredWorkerOptions: (
    companyId?: string,
    departmentId?: string,
    jobTypeId?: string,
    certificates?: string[],
    isTempWork?: boolean,
    isFreelance?: boolean,
    startDateTime?: string,
    endDateTime?: string
  ) => [
    ...QUERY_KEYS_SHIFT.shifts(),
    'preferred-worker-options',
    {
      companyId,
      departmentId,
      jobTypeId,
      certificates,
      isTempWork,
      isFreelance,
      startDateTime,
      endDateTime,
    },
  ],
  minimumRateForJobTypes: () => [...QUERY_KEYS_SHIFT.shifts(), 'minimum-rate-for-job-types'],
  partnersFilter: () => [...QUERY_KEYS_SHIFT.shifts(), 'partners-filter'],
  getShiftCreateData: () => [...QUERY_KEYS_SHIFT.shifts(), 'getShiftCreateData'],
} as const;

const QUERY_KEYS_PROJECT = {
  projects: () => [...QUERY_KEYS.all(), 'projects'],
  departmentProjects: (params: string) => [
    ...QUERY_KEYS_PROJECT.projects(),
    'departmentProjects',
    { params },
  ],
  projectDetail: (projectId: string) => [
    ...QUERY_KEYS_PROJECT.projects(),
    'projectDetail',
    { projectId },
  ],
  projectWorkers: () => [...QUERY_KEYS_PROJECT.projects(), 'workers'],
  workApplicationsForProject: (projectId: string) => [
    ...QUERY_KEYS_PROJECT.projects(),
    'project-work-applications',
    { projectId },
  ],
  projectPlanningProjectsFilters: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_PROJECT.projects(),
    'project-planning-projects',
    { params },
  ],
} as const;

const QUERY_KEYS_CHECKOUT = {
  checkouts: () => [...QUERY_KEYS.all(), 'checkouts'],
  checkoutsWithFilters: (filters: Record<string, unknown>) => [
    ...QUERY_KEYS_CHECKOUT.checkouts(),
    'checkouts',
    filters,
  ],
  checkout: (shiftClaimId: string) => [
    ...QUERY_KEYS_CHECKOUT.checkouts(),
    'checkout',
    { shiftClaimId },
  ],
  checkoutsPending: (params?: Record<string, unknown>) => [
    ...QUERY_KEYS_CHECKOUT.checkouts(),
    'pending',
    { ...params },
  ],
  checkoutsSettled: (params?: Record<string, unknown>) => [
    ...QUERY_KEYS_CHECKOUT.checkouts(),
    'settled',
    { ...params },
  ],
  checkoutInboxFilters: () => [...QUERY_KEYS_CHECKOUT.checkouts(), 'checkout-inbox-filters'],
  checkoutArchiveFilters: () => [...QUERY_KEYS_CHECKOUT.checkouts(), 'checkouts-archive-filters'],
  checkoutInboxProjectsFilters: () => [...QUERY_KEYS_CHECKOUT.checkouts(), 'checkouts-filters'],
  checkoutArchiveProjectsFilters: () => [
    ...QUERY_KEYS_CHECKOUT.checkouts(),
    'checkout-archive-projects-filters',
  ],
  projectsCheckoutsArchive: (params?: Record<string, unknown>) => [
    ...QUERY_KEYS_PROJECT.projects(),
    'project-checkouts-archive',
    { params },
  ],
  projectsCheckouts: (params?: Record<string, unknown>) => [
    ...QUERY_KEYS_PROJECT.projects(),
    'project-checkouts',
    { params },
  ],
  checkoutsMetaData: () => [...QUERY_KEYS_PROJECT.projects(), 'checkoutsMetaData'],
} as const;

const QUERY_KEYS_DEPARTMENT = {
  departments: () => [...QUERY_KEYS.all(), 'departments'],
  department: (departmentId: string) => [...QUERY_KEYS_DEPARTMENT.departments(), { departmentId }],
  departmentDetail: (departmentId: string) => [
    ...QUERY_KEYS_DEPARTMENT.departments(),
    'departmentDetail',
    { departmentId },
  ],
  departmentAsDetail: (departmentId: string) => [
    ...QUERY_KEYS_DEPARTMENT.departments(),
    'departmentAsDetail',
    { departmentId },
  ],
  departmentsWithFilters: (params: Record<string, unknown> = {}) => [
    ...QUERY_KEYS_DEPARTMENT.departments(),
    'with-filters',
    { params },
  ],
  departmentsAsUserRolesList: (params: Record<string, unknown> = {}) => [
    ...QUERY_KEYS_DEPARTMENT.departments(),
    'as-user-roles',
    { params },
  ],
  departmentsForBranchOffice: (branchOfficeId?: string) => [
    ...QUERY_KEYS_DEPARTMENT.departments(),
    'for-branch-office',
    { branchOfficeId },
  ],
  departmentsForCompany: (companyId: string) => [
    ...QUERY_KEYS_DEPARTMENT.departments(),
    'for-company',
    { companyId },
  ],
} as const;

const QUERY_KEYS_BRANCH_OFFICE = {
  branchOffices: () => [...QUERY_KEYS.all(), 'branch-offices'],
  branchOffice: (branchOfficeId: string) => [
    ...QUERY_KEYS_BRANCH_OFFICE.branchOffices(),
    { branchOfficeId },
  ],
  branchOfficesActive: () => [...QUERY_KEYS_BRANCH_OFFICE.branchOffices(), 'branch-offices-active'],
  branchOfficesForCompany: (companyId: string) => [
    ...QUERY_KEYS_BRANCH_OFFICE.branchOffices(),
    'for-company',
    { companyId },
  ],
  branchOfficesListing: () => [...QUERY_KEYS_BRANCH_OFFICE.branchOffices(), 'listing'],
  branchOfficesList: () => [...QUERY_KEYS_BRANCH_OFFICE.branchOffices(), 'list'],
  brachOfficeCreateData: () => [...QUERY_KEYS_BRANCH_OFFICE.branchOffices(), 'create-data'],
} as const;

const QUERY_KEYS_COMPANY = {
  companies: () => [...QUERY_KEYS.all(), 'companies'],
  companyBasicInfo: (companyId?: string) => [
    ...QUERY_KEYS_COMPANY.companies(),
    { companyId },
    'basic-info',
  ],
  company: (companyId: string) => [...QUERY_KEYS_COMPANY.companies(), { companyId }],
  companiesList: (filter: string | undefined) => [
    ...QUERY_KEYS_COMPANY.companies(),
    'companies-list',
    { filter },
  ],
  companyPartnersByCompany: (companyId: string) => [
    ...QUERY_KEYS_COMPANY.companies(),
    'partners',
    { companyId },
  ],
  companyGrossHourlyRate: (companyId: string) => [
    ...QUERY_KEYS_COMPANY.companies(),
    'gross-hourly-rate',
    { companyId },
  ],
  companyForEmploymentTypes: (companyId?: string) => [
    ...QUERY_KEYS_COMPANY.companies(),
    'employment-types',
    { companyId },
  ],
  companiesForFlexWorker: (workerId: string) => [
    ...QUERY_KEYS_COMPANY.companies(),
    'for-flexWorker',
    { workerId },
  ],
  companyEmploymentTypes: (companyId: string) => [
    ...QUERY_KEYS_COMPANY.companies(),
    'company-employment-types',
    { companyId },
  ],
  ownCompanyDetailAndJobGroups: () => [
    ...QUERY_KEYS_COMPANY.companies(),
    'own-company-detail-and-job-groups',
  ],
  ownCompanyEmploymentTypes: () => [
    ...QUERY_KEYS_COMPANY.companies(),
    'own-company-employment-types',
  ],
  ownCompanyTheme: () => [...QUERY_KEYS_COMPANY.companies(), 'own-company-theme'],
  billingEntities: () => [...QUERY_KEYS_COMPANY.companies(), 'billingEntities'],
  billingEntitiesListing: () => [...QUERY_KEYS_COMPANY.companies(), 'billingEntitiesListing'],
  billingEntitiesForCompany: (companyId: string) => [
    ...QUERY_KEYS_COMPANY.companies(),
    'billingEntities',
    { companyId },
  ],
  billingEntity: (id: string) => [...QUERY_KEYS_COMPANY.companies(), 'billingEntities', { id }],
  orts: () => [...QUERY_KEYS_COMPANY.companies(), 'orts'],
} as const;

const QUERY_KEYS_WORK_TEMPLATE = {
  workTemplates: () => [...QUERY_KEYS.all(), 'work-templates'],
  workTemplate: (workTemplateId: string) => [
    ...QUERY_KEYS_WORK_TEMPLATE.workTemplates(),

    { workTemplateId },
  ],
  workTemplatesForCompany: (companyId?: string) => [
    ...QUERY_KEYS_WORK_TEMPLATE.workTemplates(),
    'for-company',
    { companyId },
  ],
  workTemplateDetail: (jobId: string) => [
    ...QUERY_KEYS_WORK_TEMPLATE.workTemplates(),
    'jobDetail',
    { jobId },
  ],
  workTemplateListing: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_WORK_TEMPLATE.workTemplates(),
    'listing',
    { params },
  ],
  getWorkTemplateCreateData: () => [...QUERY_KEYS.all(), 'getWorkTemplateCreateData'],
  getWorkTemplateDefaults: () => [...QUERY_KEYS.all(), 'getWorkTemplateDefaults'],
} as const;

const QUERY_KEYS_WORK = {
  work: () => [...QUERY_KEYS.all(), 'work'],
  workDetails: (workId: string) => [QUERY_KEYS_WORK.work(), 'details', { workId }],
  workCreateData: (companyId?: string) => [...QUERY_KEYS_WORK.work(), 'create-data', { companyId }],
} as const;

const QUERY_KEYS_WORKER = {
  workers: () => [...QUERY_KEYS.all(), 'flexWorkers'],
  worker: (workerId: string) => [...QUERY_KEYS_WORKER.workers(), { workerId }],
  workersList: (filters: Record<string, unknown>) => [
    ...QUERY_KEYS_WORKER.workers(),
    'list',
    { filters },
  ],
  workersForShift: (shiftId: string, filters?: Record<string, unknown>) => [
    ...QUERY_KEYS_WORKER.workers(),
    'for-shift',
    { shiftId },
    { ...filters },
  ],
  flexWorkerBankAccounts: () => [...QUERY_KEYS_WORKER.workers(), 'bank-accounts'],
  flexWorkerProfile: (workerId: string) => [workerId, 'profile'],
  workerList: (search: string) => [...QUERY_KEYS_WORKER.workers(), 'worker-list', { search }],
  workerIdCheck: (workerId: string) => [
    ...QUERY_KEYS_WORKER.workers(),
    'worker-id-check',
    { workerId },
  ],
  flexWorkersForProject: (projectId: string, filters?: Record<string, unknown>) => [
    ...QUERY_KEYS_WORKER.workers(),
    'for-project',
    { projectId },
    { ...filters },
  ],
  flexWorkersForShiftThatWorkedForCompany: (shiftId: string, filters?: Record<string, unknown>) => [
    ...QUERY_KEYS_WORKER.workers(),
    'for-shift-that-worked-for-company',
    { shiftId },
    { ...filters },
  ],
  flexWorkerWithFlexPoolsForShift: (workerId: string, shiftId: string) => [
    ...QUERY_KEYS_WORKER.worker(workerId),
    'with-flexpools-for-shift',
    { shiftId },
  ],
  partnerWorkersFiltered: (filters?: string) => [
    ...QUERY_KEYS.workers(),
    'partner-workers-filtered',
    { filters },
  ],
  partnerWorker: (workerId: string) => [
    ...QUERY_KEYS_WORKER.workers(),
    'partner-worker',
    { workerId },
  ],
  partnerWorkerProfile: (workerId: string) => [
    ...QUERY_KEYS_WORKER.workers(),
    'partner-worker-profile',
    { workerId },
  ],
  statistics: () => [...QUERY_KEYS_WORKER.workers(), 'statistics'],
  schedule: () => [...QUERY_KEYS_WORKER.workers(), 'schedule'],
  flexWorkerSchedule: (workerId: string) => [
    ...QUERY_KEYS_WORKER.workers(),
    'schedule',
    { workerId },
  ],
} as const;

const QUERY_KEYS_PARTNER = {
  partners: () => [...QUERY_KEYS.all(), 'partners'],
  partnersList: (params?: { searchFilter?: string }) => [
    ...QUERY_KEYS_PARTNER.partners(),
    'partners-list',
    { params },
  ],
  partner: (partnerId: string) => [...QUERY_KEYS_PARTNER.partners(), { partnerId }],
  partnerRelations: (partnerId: string) => [
    ...QUERY_KEYS_PARTNER.partners(),
    'partnerRelations',
    { partnerId },
  ],
  partnerUsers: (partnerUserId: string | undefined) => [
    ...QUERY_KEYS_PARTNER.partners(),
    'partnerUsers',
    { partnerUserId },
  ],
  partnerUser: (userId: string | undefined) => [
    ...QUERY_KEYS_PARTNER.partners(),
    'partner-user',
    { userId },
  ],
  preferredWorkerProposal: () => [...QUERY_KEYS_PARTNER.partners(), 'preferred-worker-proposal'],
} as const;

const QUERY_KEYS_FLEXPOOL = {
  flexPools: () => [...QUERY_KEYS.all(), 'flex-pools'],
  flexPool: (flexPoolId: string) => [...QUERY_KEYS_FLEXPOOL.flexPools(), { flexPoolId }],
  flexPoolList: () => [...QUERY_KEYS_FLEXPOOL.flexPools(), 'flex-pool-list'],
  flexPoolsForFlexWorker: (flexWorkerId: string) => [
    ...QUERY_KEYS_FLEXPOOL.flexPools(),
    'for-flexWorker',
    { flexWorkerId },
  ],
  flexPoolsWithParams: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_FLEXPOOL.flexPools(),
    'flexPoolsWithParams',
    { params },
  ],
  flexPoolJobs: (jobId: string) => [...QUERY_KEYS_FLEXPOOL.flexPools(), 'jobs', { jobId }],
  flexPoolInternalListWithParams: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_FLEXPOOL.flexPools(),
    'flex-pool-internal-list',
    { params },
  ],
  flexPoolExternalListWithParams: () => [
    ...QUERY_KEYS_FLEXPOOL.flexPools(),
    'flex-pool-external-list',
  ],
  flexPoolWithFlexWorkers: (flexPoolId: string) => [
    ...QUERY_KEYS_FLEXPOOL.flexPools(),
    'with-flex-workers',
    { flexPoolId },
  ],
  flexPoolForFlexworker: (flexPoolId: string, flexWorkerName?: string) => [
    ...QUERY_KEYS_FLEXPOOL.flexPools(),
    'flexPoolForFlexworker',
    { flexPoolId, flexWorkerName },
  ],
  flexPoolAvailableWorkers: (flexPoolId: string, filter: string) => [
    ...QUERY_KEYS_FLEXPOOL.flexPools(),
    'available-flex-workers',
    { flexPoolId, filter },
  ],
  flexPoolsForCompany: () => [...QUERY_KEYS_FLEXPOOL.flexPools(), 'company'],
  flexpoolManagementData: () => [...QUERY_KEYS_FLEXPOOL.flexPools(), 'flexpool-management-data'],
  externalWorkersPools: () => [...QUERY_KEYS_FLEXPOOL.flexPools(), 'external-workers-pools'],
  externalWorkersPoolDetail: (id: string) => [
    ...QUERY_KEYS_FLEXPOOL.externalWorkersPools(),
    'detail',
    { id },
  ],
  externalWorkersPoolFlexWorkers: (workersPoolId: string, flexWorkerName?: string) => [
    ...QUERY_KEYS_FLEXPOOL.externalWorkersPools(),
    'flex-workers',
    { workersPoolId, flexWorkerName },
  ],
  externalWorkersPoolAvailable: (workersPoolId: string, filter: string) => [
    ...QUERY_KEYS_FLEXPOOL.externalWorkersPools(),
    'available',
    { workersPoolId, filter },
  ],
} as const;

const QUERY_KEYS_PLACEMENT = {
  placements: () => [...QUERY_KEYS.all(), 'placements'],
  placementsWithFilters: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_PLACEMENT.placements(),
    'placements-with-filters',
    { params },
  ],
} as const;

const QUERY_KEYS_USERS = {
  users: () => [...QUERY_KEYS.all(), 'users'],
  user: (userId: string) => [...QUERY_KEYS_USERS.users(), { userId }],
  ogpUserInfo: () => [...QUERY_KEYS_USERS.users(), 'ogp', 'user-info'],
  hqUserInfo: () => [...QUERY_KEYS_USERS.users(), 'hq', 'user-info'],
  partnerUserInfo: () => [...QUERY_KEYS_USERS.users(), 'partner', 'user-info'],
  rolesMatrix: () => [...QUERY_KEYS_USERS.users(), 'roles-matrix'],
} as const;

const QUERY_KEYS_FILTER = {
  filters: () => [...QUERY_KEYS.all(), 'filters'],
  businessUnits: () => [...QUERY_KEYS_FILTER.filters(), 'business-units'],
  jobTypes: () => [...QUERY_KEYS_FILTER.filters(), 'job-types'],
  jobTypesList: (companyId?: string, branchOfficeId?: string) => [
    ...QUERY_KEYS_FILTER.filters(),
    'jobTypesList',
    { companyId, branchOfficeId },
  ],
  filterWorkersData: () => [...QUERY_KEYS_FILTER.filters(), 'filters-workers'],
} as const;

const QUERY_KEYS_INBOX = {
  inbox: () => [...QUERY_KEYS.all(), 'inbox'],
  notifications: () => [...QUERY_KEYS_INBOX.inbox(), 'notifications'],
  inboxMessages: () => [...QUERY_KEYS_INBOX.inbox(), 'messages'],
  inboxMessage: (messageId: string) => [...QUERY_KEYS_INBOX.inbox(), 'messages', { messageId }],
} as const;

const QUERY_KEYS_ACCREDITATION = {
  accreditations: () => [...QUERY_KEYS.all(), 'accreditations'],
  certificates: () => [...QUERY_KEYS_ACCREDITATION.accreditations(), 'certificates'],
  accreditationDetail: () => [...QUERY_KEYS_ACCREDITATION.accreditations(), 'accreditationDetail'],
  accreditationsWithFilters: (filterParams: Record<string, unknown>) => [
    ...QUERY_KEYS_ACCREDITATION.accreditations(),
    'accreditationsWithFilters',
    { filterParams },
  ],
  accreditationsCertificateNames: () => [
    ...QUERY_KEYS_ACCREDITATION.accreditations(),
    'accreditationsCertificateNames',
  ],
  accreditationsCertificates: () => [
    ...QUERY_KEYS_ACCREDITATION.accreditations(),
    'accreditationsCertificates',
  ],
  accreditationsProfile: () => [
    ...QUERY_KEYS_ACCREDITATION.accreditations(),
    'accreditationsProfile',
  ],
  requiredCertificatesForJobType: (jobTypeId: string) => [
    ...QUERY_KEYS_ACCREDITATION.accreditations(),
    'required-certificates-for-job-type',
    { jobTypeId },
  ],
  employeeCertificates: (flexWorkerId: string) => [
    ...QUERY_KEYS_ACCREDITATION.accreditations(),
    'employeeCertificates',
    { flexWorkerId },
  ],
} as const;

const QUERY_KEYS_COUNTER_OFFER = {
  counterOffers: () => [...QUERY_KEYS.all(), 'counterOffers'],
  counterOffersWithFilters: (filters: Record<string, unknown>) => [
    ...QUERY_KEYS_COUNTER_OFFER.counterOffers(),
    'counterOffersWithFilters',
    { filters },
  ],
  counterOffer: (shiftClaimId: string) => [
    ...QUERY_KEYS.counterOffers(),
    'counterOffer',
    { shiftClaimId },
  ],
} as const;

const QUERY_KEYS_ENUMS = {
  noShowReasons: () => [...QUERY_KEYS_WORKER.workers(), 'no-show-reasons'],
} as const;

const QUERY_KEYS_SYSTEM = {
  system: () => [...QUERY_KEYS.all(), 'system'],
  platformSettings: () => [...QUERY_KEYS_SYSTEM.system(), 'platformSettings'],
  environmentConfiguration: () => [...QUERY_KEYS_SYSTEM.system(), 'environment-configuration'],
  featureFlags: () => [...QUERY_KEYS_SYSTEM.system(), 'feature-flags'],
  loginConfig: () => [...QUERY_KEYS_SYSTEM.system(), 'login-config'],
  version: () => [...QUERY_KEYS_SYSTEM.system(), 'version'],
  fallbackWorkImage: () => [...QUERY_KEYS_SYSTEM.system(), 'fallback-work-image'],
} as const;

const QUERY_KEYS_NOT_WELCOME_LIST = {
  notWelcomeList: () => [...QUERY_KEYS.all(), 'not-welcome-list'],
} as const;

const QUERY_KEYS_FINANCE = {
  payments: () => [...QUERY_KEYS.all(), 'payments'],
  paymentsWithFilters: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_FINANCE.payments(),
    'payments',
    { params },
  ],
  paymentUsers: (params: Record<string, unknown>) => [
    ...QUERY_KEYS_FINANCE.payments(),
    'payment-users',
    { params },
  ],
};

const QUERY_KEYS_DASHBOARD_ANALYTICS = {
  applicationsPendingReview: () => [...QUERY_KEYS.all(), 'applications-pending-review'],
  applicationAverageReactionTime: () => [...QUERY_KEYS.all(), 'application-average-reaction-time'],
  checkoutsPendingReview: () => [...QUERY_KEYS.all(), 'checkouts-pending-review'],
  checkoutsAverageAcceptanceTime: () => [...QUERY_KEYS.all(), 'checkouts-average-acceptance-time'],
  fillRateOfUpcomingShifts: () => [...QUERY_KEYS.all(), 'fill-rate-of-upcoming-shifts'],
  fillRateForFlexPoolWorkers: () => [...QUERY_KEYS.all(), 'fill-rate-for-flex-pool-workers'],
  medianShiftPublishingWindow: () => [...QUERY_KEYS.all(), 'median-shift-publishing-window'],
  daysPlannedAhead: () => [...QUERY_KEYS.all(), 'days-planned-ahead'],
} as const;

const QUERY_KEYS_DASHBOARD_TODO_LIST = {
  unfulfilledShifts: () => [...QUERY_KEYS.all(), 'unfulfilled-shifts'],
  openCheckouts: () => [...QUERY_KEYS.all(), 'open-checkouts'],
  notPlannedWork: () => [...QUERY_KEYS.all(), 'not-planned-work'],
} as const;

const QUERY_KEYS_DASHBOARD_CHART = {
  dashboardChart: () => [...QUERY_KEYS.all(), 'shift-overview'],
} as const;

const QUERY_KEYS_WORKFORCE = {
  workforce: () => [...QUERY_KEYS.all(), 'workforce'],
  employee: (employeeId: string) => [...QUERY_KEYS_WORKFORCE.workforce(), { employeeId }],
} as const;

const QUERY_KEYS_AVAILABILITY = {
  availability: () => [...QUERY_KEYS.all(), 'availability'],
  workerAvailability: (workerId: string, fromDate: string) => [
    ...QUERY_KEYS_AVAILABILITY.availability(),
    'worker',
    { workerId, fromDate },
  ],
  workersAvailability: (search: string | undefined, fromDate: string) => [
    ...QUERY_KEYS_AVAILABILITY.availability(),
    'workersAvailability',
    { search, fromDate },
  ],
} as const;

const QUERY_KEYS_DOCUMENTS = {
  documents: () => [...QUERY_KEYS.all(), 'documents'],
  documentsWithFilters: (filterParams: Record<string, unknown>) => [
    ...QUERY_KEYS_DOCUMENTS.documents(),
    'documents-with-filters',
    { filterParams },
  ],
  renderDocument: (documentId: string) => [
    ...QUERY_KEYS_DOCUMENTS.documents(),
    'render-document',
    { documentId },
  ],
  documentDetail: (documentId: string) => [
    ...QUERY_KEYS_DOCUMENTS.documents(),
    'document-detail',
    { documentId },
  ],
  documentForFlexWorker: (workerId: string, documentType: string) => [
    ...QUERY_KEYS_DOCUMENTS.documents(),
    'document-for-flexworker',
    { workerId, documentType },
  ],
} as const;

const QUERY_KEYS_DOCUMENT_MODELS = {
  documentModels: () => [...QUERY_KEYS.all(), 'document-models'],
} as const;

const QUERY_KEYS_WORKFLOW = {
  workflows: () => [...QUERY_KEYS.all(), 'workflows'],
  workflowDetails: (workflowId: string) => [...QUERY_KEYS_WORKFLOW.workflows(), { workflowId }],
  workflowsListing: () => [...QUERY_KEYS_WORKFLOW.workflows(), 'listing'],
} as const;

export const QUERY_KEYS_SERVICE_AGREEMENTS = {
  serviceAgreementsForCompany: () => [...QUERY_KEYS.all(), 'service-agreements-for-company'],
  detail: (id: string) => [
    ...QUERY_KEYS_SERVICE_AGREEMENTS.serviceAgreementsForCompany(),
    'detail',
    { id },
  ],
} as const;

export const QUERY_KEYS_PUBLIC = {
  publicCertificates: () => [...QUERY_KEYS.all(), 'public-certificates'],
  workerById: (workerId: string | undefined, level: string, verified?: boolean) => [
    ...QUERY_KEYS.all(),
    'public-worker',
    { workerId, verified, level },
  ],
} as const;

export const QUERY_KEYS = {
  all: () => ['all'],
  ...QUERY_KEYS_SHIFT,
  ...QUERY_KEYS_DEPARTMENT,
  ...QUERY_KEYS_BRANCH_OFFICE,
  ...QUERY_KEYS_COMPANY,
  ...QUERY_KEYS_WORK_TEMPLATE,
  ...QUERY_KEYS_WORKER,
  ...QUERY_KEYS_FLEXPOOL,
  ...QUERY_KEYS_WORK_TEMPLATE,
  ...QUERY_KEYS_WORK,
  ...QUERY_KEYS_CHECKOUT,
  ...QUERY_KEYS_PROJECT,
  ...QUERY_KEYS_PARTNER,
  ...QUERY_KEYS_FILTER,
  ...QUERY_KEYS_PLACEMENT,
  ...QUERY_KEYS_USERS,
  ...QUERY_KEYS_INBOX,
  ...QUERY_KEYS_ACCREDITATION,
  ...QUERY_KEYS_COUNTER_OFFER,
  ...QUERY_KEYS_SYSTEM,
  ...QUERY_KEYS_ENUMS,
  ...QUERY_KEYS_NOT_WELCOME_LIST,
  ...QUERY_KEYS_FINANCE,
  ...QUERY_KEYS_DASHBOARD_ANALYTICS,
  ...QUERY_KEYS_DASHBOARD_TODO_LIST,
  ...QUERY_KEYS_DASHBOARD_CHART,
  ...QUERY_KEYS_WORKFORCE,
  ...QUERY_KEYS_AVAILABILITY,
  ...QUERY_KEYS_DOCUMENT_MODELS,
  ...QUERY_KEYS_DOCUMENTS,
  ...QUERY_KEYS_WORKFLOW,
  ...QUERY_KEYS_SERVICE_AGREEMENTS,
  ...QUERY_KEYS_PUBLIC,
} as const;
